import { useEffect, useState, useMemo } from "react";
import Layout from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Pencil, Trash2, ShieldCheck, ArrowUp, ArrowDown, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const PERM_LABELS = {
    dashboard: "Dashboard",
    contacts_view: "Ver contactos", contacts_manage: "Gestionar contactos", contacts_global_view: "Ver todos los contactos",
    companies_view: "Ver empresas", companies_manage: "Gestionar empresas", companies_global_view: "Ver todas las empresas",
    deals_view: "Ver deals", deals_manage: "Gestionar deals", deals_global_view: "Ver todos los deals",
    funnels_view: "Ver embudos", funnels_manage: "Gestionar embudos",
    tasks_view: "Ver tareas", tasks_manage: "Gestionar tareas",
    calendar_view: "Ver calendario", calendar_manage: "Gestionar calendario",
    weekly_control_view: "Ver Tráfico semanal", weekly_control_manage: "Gestionar Tráfico semanal",
    service_financial_view: "Ver Control financiero", service_financial_manage: "Gestionar Control financiero",
    service_weekly_audit_view: "Ver Auditoría semanal", service_weekly_audit_manage: "Gestionar Auditoría semanal",
    service_backups_view: "Ver BackUps", service_backups_manage: "Gestionar BackUps",
    code_security_view: "Ver Código y Seguridad", code_security_manage: "Gestionar Código y Seguridad",
    service_credentials_view: "Ver credenciales de servicios", service_credentials_manage: "Gestionar credenciales de servicios",
    ai_usage: "Ver Consumo IA", ai_usage_manage: "Gestionar aplicaciones y claves IA",
    admin: "Administración", audit: "Logs/Auditoría",
};

const PERMISSION_GROUPS = [
    ["General", ["dashboard"]],
    ["Contactos", ["contacts_view", "contacts_manage", "contacts_global_view"]],
    ["Empresas", ["companies_view", "companies_manage", "companies_global_view"]],
    ["Deals", ["deals_view", "deals_manage", "deals_global_view"]],
    ["Embudos", ["funnels_view", "funnels_manage"]],
    ["Tareas", ["tasks_view", "tasks_manage"]],
    ["Calendario", ["calendar_view", "calendar_manage"]],
    ["Tráfico semanal", ["weekly_control_view", "weekly_control_manage"]],
    ["Control financiero", ["service_financial_view", "service_financial_manage"]],
    ["Auditoría semanal", ["service_weekly_audit_view", "service_weekly_audit_manage"]],
    ["BackUps", ["service_backups_view", "service_backups_manage"]],
    ["Código y Seguridad", ["code_security_view", "code_security_manage"]],
    ["Credenciales", ["service_credentials_view", "service_credentials_manage"]],
    ["Consumo IA", ["ai_usage", "ai_usage_manage"]],
    ["Administración", ["admin", "audit"]],
];

export default function Admin() {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [perms, setPerms] = useState([]);

    const loadAll = async () => {
        const [u, r, p] = await Promise.all([
            api.get("/admin/users"), api.get("/admin/roles"), api.get("/admin/roles/permissions"),
        ]);
        setUsers(u.data); setRoles(r.data); setPerms(p.data.permissions);
    };
    useEffect(() => { loadAll(); }, []);

    return (
        <Layout title="Administración" subtitle="Usuarios, Roles y Permisos">
            <Tabs defaultValue="users">
                <TabsList>
                    <TabsTrigger value="users" data-testid="tab-users">Usuarios</TabsTrigger>
                    <TabsTrigger value="roles" data-testid="tab-roles">Roles y Permisos</TabsTrigger>
                </TabsList>
                <TabsContent value="users" className="mt-4">
                    <UsersTab users={users} roles={roles} reload={loadAll}/>
                </TabsContent>
                <TabsContent value="roles" className="mt-4">
                    <RolesTab roles={roles} perms={perms} reload={loadAll}/>
                </TabsContent>
            </Tabs>
        </Layout>
    );
}

function UsersTab({ users, roles, reload }) {
    const { user: me } = useAuth();
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({});
    const [sort, setSort] = useState({ key: null, dir: 0 });
    const [selected, setSelected] = useState(new Set());
    const [q, setQ] = useState("");

    const openNew = () => { setEditing(null); setForm({ role: "vendedor", active: true }); setOpen(true); };
    const openEdit = (u) => { setEditing(u); setForm({ ...u, password: "" }); setOpen(true); };

    const save = async () => {
        try {
            if (editing) {
                const payload = { ...form }; if (!payload.password) delete payload.password;
                await api.patch(`/admin/users/${editing.user_id}`, payload);
            } else { await api.post("/admin/users", form); }
            toast.success("Guardado"); setOpen(false); reload();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const remove = async (id) => { if (!window.confirm("¿Eliminar usuario?")) return; await api.delete(`/admin/users/${id}`); toast.success("Eliminado"); reload(); };

    const cycleSort = (key) => {
        setSort((s) => {
            if (s.key !== key) return { key, dir: 1 };
            if (s.dir === 1) return { key, dir: 2 };
            return { key: null, dir: 0 };
        });
    };
    const sortIcon = (key) => {
        if (sort.key !== key) return null;
        return sort.dir === 1 ? <ArrowUp className="w-3 h-3 inline ml-1"/> : <ArrowDown className="w-3 h-3 inline ml-1"/>;
    };

    const sorted = useMemo(() => {
        const norm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const term = norm(q.trim());
        const filtered = !term ? users : users.filter(u => {
            const roleName = roles.find(r => r.slug === u.role)?.name || u.role || "";
            return norm(u.name).startsWith(term)
                || norm(u.email).startsWith(term)
                || norm(roleName).startsWith(term);
        });
        if (!sort.key || sort.dir === 0) return filtered;
        const valOf = (u) => {
            if (sort.key === "role") return (roles.find(r => r.slug === u.role)?.name || u.role || "");
            if (sort.key === "active") return u.active === false ? "0" : "1";
            return (u[sort.key] || "");
        };
        return [...filtered].sort((a, b) => {
            const av = valOf(a).toString().toLowerCase();
            const bv = valOf(b).toString().toLowerCase();
            return sort.dir === 1 ? av.localeCompare(bv) : bv.localeCompare(av);
        });
    }, [users, sort, roles, q]);

    const allSelected = sorted.length > 0 && sorted.every(u => selected.has(u.user_id) || u.user_id === me?.user_id);
    const toggleAll = () => {
        const next = new Set(selected);
        if (allSelected) sorted.forEach(u => next.delete(u.user_id));
        else sorted.forEach(u => { if (u.user_id !== me?.user_id) next.add(u.user_id); });
        setSelected(next);
    };
    const toggleOne = (id) => {
        if (id === me?.user_id) return; // never select self
        const next = new Set(selected);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelected(next);
    };

    const bulkDelete = async () => {
        const ids = Array.from(selected);
        if (ids.length === 0) return;
        if (!window.confirm(`¿Eliminar ${ids.length} usuario(s) seleccionado(s)? Esta acción es irreversible.`)) return;
        try {
            const { data } = await api.post("/admin/users/bulk-delete", { user_ids: ids });
            toast.success(`Eliminados: ${data.deleted}`);
            setSelected(new Set());
            reload();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const Th = ({ k, label }) => (
        <th
            className="text-left px-4 py-3 cursor-pointer select-none hover:text-orange-600 transition-colors"
            onClick={() => cycleSort(k)}
            data-testid={`users-sort-${k}`}
        >
            {label}{sortIcon(k)}
        </th>
    );

    return (
        <div>
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                {selected.size > 0 ? (
                    <Button variant="outline" onClick={bulkDelete} className="text-red-600 hover:text-red-700 border-red-300" data-testid="users-bulk-delete-button">
                        <Trash2 className="w-4 h-4 mr-2"/>Eliminar seleccionados ({selected.size})
                    </Button>
                ) : <div/>}
                <Button onClick={openNew} data-testid="user-new-button"><Plus className="w-4 h-4 mr-2"/>Crear Usuario</Button>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <div className="p-3 border-b border-slate-200 flex items-center gap-2">
                    <Search className="w-4 h-4 text-slate-400"/>
                    <Input
                        value={q}
                        onChange={(e)=>setQ(e.target.value)}
                        placeholder="Buscar por nombre, email o rol (prefijo)..."
                        className="border-0 focus-visible:ring-0 shadow-none"
                        data-testid="users-search-input"
                    />
                </div>
                <table className="w-full text-sm">
                    <thead><tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                        <th className="px-3 py-3 w-10">
                            <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="users-select-all" aria-label="Seleccionar todos"/>
                        </th>
                        <Th k="name" label="Nombre"/>
                        <Th k="email" label="Email"/>
                        <Th k="role" label="Rol"/>
                        <Th k="active" label="Estado"/>
                        <th className="text-right px-4 py-3">Acciones</th>
                    </tr></thead>
                    <tbody>
                        {sorted.map(u => {
                            const role = roles.find(r => r.slug === u.role);
                            const isMe = u.user_id === me?.user_id;
                            return (
                            <tr key={u.user_id} className={`border-t border-slate-100 hover:bg-slate-50 ${selected.has(u.user_id) ? "bg-orange-50/40" : ""}`} data-testid={`user-row-${u.user_id}`}>
                                <td className="px-3 py-3">
                                    <Checkbox
                                        checked={selected.has(u.user_id)}
                                        onCheckedChange={()=>toggleOne(u.user_id)}
                                        disabled={isMe}
                                        data-testid={`user-select-${u.user_id}`}
                                        aria-label="Seleccionar usuario"
                                    />
                                </td>
                                <td className="px-4 py-3 font-medium text-slate-900">{u.name}{isMe && <span className="ml-2 text-[10px] uppercase text-orange-600">tú</span>}</td>
                                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                                <td className="px-4 py-3">{role?.name || u.role}</td>
                                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${u.active!==false?"bg-green-50 text-green-700":"bg-slate-100 text-slate-500"}`}>{u.active!==false?"Activo":"Inactivo"}</span></td>
                                <td className="px-4 py-3 text-right">
                                    <Button variant="ghost" size="icon" onClick={()=>openEdit(u)}><Pencil className="w-4 h-4"/></Button>
                                    <Button variant="ghost" size="icon" onClick={()=>remove(u.user_id)}><Trash2 className="w-4 h-4 text-red-500"/></Button>
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{editing ? "Editar" : "Nuevo"} usuario</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label>Nombre</Label><Input value={form.name||""} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="user-form-name"/></div>
                        {!editing && <div><Label>Email</Label><Input type="email" value={form.email||""} onChange={(e)=>setForm({...form, email: e.target.value})} data-testid="user-form-email"/></div>}
                        <div><Label>Rol</Label>
                            <Select value={form.role||"vendedor"} onValueChange={(v)=>setForm({...form, role: v})}>
                                <SelectTrigger data-testid="user-form-role"><SelectValue/></SelectTrigger>
                                <SelectContent>{roles.map(r => <SelectItem key={r.slug} value={r.slug}>{r.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div><Label>Contraseña {editing && "(vacío = no cambiar)"}</Label><Input type="password" value={form.password||""} onChange={(e)=>setForm({...form, password: e.target.value})} data-testid="user-form-password"/></div>
                        {editing && <div className="flex items-center gap-2"><Switch checked={form.active!==false} onCheckedChange={(v)=>setForm({...form, active: v})}/><Label>Cuenta activa</Label></div>}
                    </div>
                    <DialogFooter><Button variant="ghost" onClick={()=>setOpen(false)}>Cancelar</Button><Button onClick={save} data-testid="user-form-save">Guardar</Button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function RolesTab({ roles, perms, reload }) {
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: "", description: "", permissions: [] });

    const openNew = () => { setEditing(null); setForm({ name: "", description: "", permissions: ["dashboard"] }); setOpen(true); };
    const openEdit = (r) => { setEditing(r); setForm({ name: r.name, description: r.description || "", permissions: r.permissions || [] }); setOpen(true); };

    const togglePerm = (p) => {
        setForm((f) => ({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter(x=>x!==p) : [...f.permissions, p] }));
    };

    const save = async () => {
        try {
            if (editing) await api.patch(`/admin/roles/${editing.role_id}`, form);
            else await api.post("/admin/roles", form);
            toast.success("Guardado"); setOpen(false); reload();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const remove = async (r) => {
        if (!window.confirm(`¿Eliminar rol "${r.name}"?`)) return;
        try { await api.delete(`/admin/roles/${r.role_id}`); toast.success("Eliminado"); reload(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    return (
        <div>
            <div className="flex justify-end mb-3">
                <Button onClick={openNew} data-testid="role-new-button"><Plus className="w-4 h-4 mr-2"/>Crear Rol</Button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
                {roles.map((r) => (
                    <div key={r.role_id} className="bg-white border border-slate-200 rounded-lg p-5" data-testid={`role-card-${r.slug}`}>
                        <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <div className="w-9 h-9 rounded-md bg-orange-50 text-orange-600 flex items-center justify-center"><ShieldCheck className="w-4 h-4"/></div>
                                <div>
                                    <h3 className="font-display font-semibold text-slate-900">{r.name}</h3>
                                    <div className="text-xs text-slate-500 font-mono">{r.slug}{r.protected && <span className="ml-2 text-amber-600">(protegido)</span>}</div>
                                </div>
                            </div>
                            <div className="flex gap-1">
                                <Button variant="ghost" size="icon" onClick={()=>openEdit(r)} data-testid={`role-edit-${r.slug}`}><Pencil className="w-4 h-4"/></Button>
                                {!r.protected && <Button variant="ghost" size="icon" onClick={()=>remove(r)}><Trash2 className="w-4 h-4 text-red-500"/></Button>}
                            </div>
                        </div>
                        {r.description && <p className="text-sm text-slate-600 mb-3">{r.description}</p>}
                        <div className="flex flex-wrap gap-1.5">
                            {(r.permissions||[]).map((p) => (
                                <span key={p} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-orange-50 text-orange-700">{PERM_LABELS[p]||p}</span>
                            ))}
                            {(r.permissions||[]).length === 0 && <span className="text-xs text-slate-400">Sin permisos</span>}
                        </div>
                    </div>
                ))}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{editing ? "Editar" : "Crear"} rol</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div><Label>Nombre</Label><Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="role-form-name" disabled={editing?.protected}/></div>
                        <div><Label>Descripción</Label><Input value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})}/></div>
                        <div>
                            <Label className="mb-2 block">Permisos</Label>
                            <div className="max-h-[52vh] overflow-y-auto rounded-lg border border-slate-200 p-3 space-y-4">
                                {PERMISSION_GROUPS.map(([group, groupPerms]) => {
                                    const available = groupPerms.filter((permission) => perms.includes(permission));
                                    if (!available.length) return null;
                                    return <section key={group}>
                                        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{group}</h4>
                                        <div className="grid sm:grid-cols-2 gap-1.5">
                                            {available.map((permission) => <label key={permission} className="flex items-center gap-2 text-sm p-2 rounded hover:bg-slate-50 cursor-pointer">
                                                <Checkbox checked={form.permissions.includes(permission)} onCheckedChange={()=>togglePerm(permission)} data-testid={`role-perm-${permission}`}/>
                                                {PERM_LABELS[permission] || permission}
                                            </label>)}
                                        </div>
                                    </section>;
                                })}
                            </div>
                        </div>
                    </div>
                    <DialogFooter><Button variant="ghost" onClick={()=>setOpen(false)}>Cancelar</Button><Button onClick={save} data-testid="role-form-save">Guardar</Button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
